import debounce from "lodash/debounce";
import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useLocation } from "react-router-dom";
import {
  Virtuoso,
  type Components,
  type VirtuosoProps,
  type VirtuosoHandle,
  type ListRange,
  type IndexLocationWithAlign,
} from "react-virtuoso";

import { Spinner, Text } from "./ui";

/** Custom Viruoso component context. */
type Context = {
  itemClassName?: string;
  listClassName?: string;
};

/** Scroll position saved in sessionStorage. */
type SavedScrollPosition = {
  index: number;
  offset: number;
  /** History entry the position was saved on. */
  locationKey: string;
};

const scrollDataKeyFor = (scrollKey = "") => `truth:scrollData:${scrollKey}`;

/**
 * Position saved for `scrollKey`, but only when it belongs to the history entry
 * the caller is on — saved positions outlive the visit that wrote them, and
 * arriving at a route anew has to start at the top.
 */
const readSavedScrollPosition = (
  scrollKey: string | undefined,
  locationKey: string,
): SavedScrollPosition | null => {
  const saved = scrollKey
    ? sessionStorage.getItem(scrollDataKeyFor(scrollKey))
    : null;
  if (!saved) return null;

  const data: SavedScrollPosition = JSON.parse(saved);
  return data?.locationKey === locationKey ? data : null;
};

/** Custom Virtuoso Item component representing a single scrollable item. */
// NOTE: It's crucial to space lists with **padding** instead of margin!
// Pass an `itemClassName` like `pb-3`, NOT a `space-y-3` className
// https://virtuoso.dev/troubleshooting#list-does-not-scroll-to-the-bottom--items-jump-around
const Item: Components<React.JSX.Element, Context>["Item"] = ({
  context,
  ...rest
}) => <div className={context?.itemClassName} {...rest} />;

/** Custom Virtuoso List component for the outer container. */
// Ensure the className winds up here
const List: Components<React.JSX.Element, Context>["List"] = (props) => {
  const { context, ref, ...rest } = props;
  return <div ref={ref} className={context?.listClassName} {...rest} />;
};

interface ScrollableListProps extends VirtuosoProps<any, any> {
  /** Unique key to preserve the scroll position when navigating back. */
  scrollKey?: string;
  /** Pagination callback when the end of the list is reached. */
  onLoadMore?: () => void;
  /** Whether the data is currently being fetched. */
  isLoading?: boolean;
  /** Whether we expect an additional page of data. */
  hasMore?: boolean;
  /** Additional element to display at the top of the list. */
  prepend?: React.ReactNode;
  /** Additional element to display at the bottom of the list. */
  append?: React.ReactNode;
  /** Whether to display the prepended element. */
  alwaysPrepend?: boolean;
  /** Message to display when the list is loaded but empty. */
  emptyMessage?: React.ReactNode;
  /**
   * Custom component to display when the list is loaded but empty. Pass `null`
   * to render nothing at all — the default `emptyMessage` chrome (a full-height
   * centred, padded box) is skipped entirely. An empty fragment does NOT do
   * this: it still renders that chrome around nothing.
   */
  emptyComponent?: React.ReactNode;
  /** Scrollable content. */
  children: Iterable<React.ReactNode>;
  /** Callback when the list is scrolled to the top. */
  onScrollToTop?: () => void;
  /** Callback when the list is scrolled. */
  onScroll?: () => void;
  /** Placeholder component to render while loading. */
  placeholderComponent?: React.ComponentType | React.NamedExoticComponent;
  /** Number of placeholders to render while loading. */
  placeholderCount?: number;
  /**
   * Pull to refresh callback.
   * @deprecated Put a PTR around the component instead.
   */
  onRefresh?: () => Promise<any>;
  /** Extra class names on the Virtuoso element. */
  className?: string;
  /** Class names on each item container. */
  itemClassName?: string;
  /** `id` attribute on the Virtuoso element. */
  id?: string;
  /** CSS styles on the Virtuoso element. */
  style?: React.CSSProperties;
  /** Whether to use the window to scroll the content instead of Virtuoso's container. */
  useWindowScroll?: boolean;
  ref?: React.Ref<VirtuosoHandle>;
}

/** Legacy ScrollableList with Virtuoso for backwards-compatibility. */
const ScrollableList = ({
  atTopStateChange,
  atTopThreshold,
  atBottomStateChange,
  scrollKey,
  prepend = null,
  append = null,
  alwaysPrepend,
  children,
  isLoading,
  emptyMessage,
  emptyComponent,
  onRefresh,
  onScroll,
  onScrollToTop,
  onLoadMore,
  className,
  itemClassName,
  id,
  hasMore,
  placeholderComponent: Placeholder,
  placeholderCount = 3,
  initialTopMostItemIndex = 0,
  style = {},
  useWindowScroll = true,
  firstItemIndex = 0,
  increaseViewportBy,
  ref,
}: ScrollableListProps) => {
  const locationKey = useLocation().key ?? "default";

  const scrollDataKey = scrollDataKeyFor(scrollKey);
  const restoredScrollData = useMemo(
    () => readSavedScrollPosition(scrollKey, locationKey),
    [scrollKey, locationKey],
  );

  const topIndex = useRef<number>(restoredScrollData?.index ?? 0);
  const topOffset = useRef<number>(restoredScrollData?.offset ?? 0);

  const [range, setRange] = useState<ListRange>({
    startIndex: 0,
    endIndex: 0,
  });
  const hotkeyIndex = useRef<number>(0);

  /** Normalized children. */
  const elements = Array.from(children || []);

  const showPlaceholder = isLoading && Placeholder && placeholderCount > 0;

  // NOTE: We are doing some trickery to load a feed of placeholders
  // Virtuoso's `EmptyPlaceholder` unfortunately doesn't work for our use-case
  const data = showPlaceholder ? Array(placeholderCount).fill("") : elements;

  // Add a placeholder at the bottom for loading
  // (Don't use Virtuoso's `Footer` component because it doesn't preserve its height)
  if (hasMore && Placeholder) {
    data.push(<Placeholder />);
  } else if (hasMore) {
    data.push(
      <div className="flex h-full justify-center py-4">
        <Spinner />
      </div>,
    );
  }

  const handleScroll = useCallback(
    debounce(
      () => {
        // HACK: Virtuoso has no better way to get this...
        const node = document.querySelector(
          `[data-virtuoso-scroller] [data-item-index="${topIndex.current}"]`,
        );
        if (node) {
          topOffset.current = node.getBoundingClientRect().top * -1;
        } else {
          topOffset.current = 0;
        }
      },
      150,
      { trailing: true },
    ),
    [],
  );

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollKey) {
        const data: SavedScrollPosition = {
          index: topIndex.current,
          offset: topOffset.current,
          locationKey,
        };
        sessionStorage.setItem(scrollDataKey, JSON.stringify(data));
      }
      document.removeEventListener("scroll", handleScroll);
      handleScroll.cancel();
    };
  }, [handleScroll, scrollDataKey, scrollKey, locationKey]);

  /* Render an empty state instead of the scrollable list. */
  const renderEmpty = (): React.JSX.Element => {
    if (emptyComponent !== undefined) {
      if (isLoading)
        return (
          <div className="flex h-full justify-center py-4">
            <Spinner />
          </div>
        );
      return <>{emptyComponent}</>;
    }
    return (
      <div className="flex h-full justify-center py-4">
        {alwaysPrepend && prepend}

        {isLoading ? (
          <Spinner />
        ) : (
          <div className="p-4">
            <Text tag="span">{emptyMessage}</Text>
          </div>
        )}
      </div>
    );
  };

  /** Render a single item. */
  const renderItem = (
    _i: number,
    element: React.JSX.Element,
  ): React.JSX.Element => {
    if (showPlaceholder) {
      return <Placeholder />;
    } else {
      return element;
    }
  };

  const handleEndReached = () => {
    if (hasMore && onLoadMore) {
      onLoadMore();
    }
  };

  const handleRangeChange = (range: ListRange) => {
    // HACK: using the first index can be buggy.
    // Track the second item instead, unless the endIndex comes before it (eg one 1 item in view).
    setRange(range);
    topIndex.current = Math.min(range.startIndex + 1, range.endIndex);
    handleScroll();
  };

  /** Figure out the initial index to scroll to. */
  const initialIndex = useMemo<number | IndexLocationWithAlign>(() => {
    if (isLoading) return 0;
    if (initialTopMostItemIndex) return initialTopMostItemIndex;
    if (restoredScrollData) {
      return {
        align: "start",
        index: restoredScrollData.index,
        offset: restoredScrollData.offset,
      };
    }

    return 0;
  }, [isLoading, initialTopMostItemIndex, restoredScrollData]);

  const handleHotkeyScroll = (direction: "up" | "down") => {
    const isDown = direction === "down";
    let index = hotkeyIndex.current;

    if (index === 0 || index < range.startIndex) {
      index = range.startIndex + (isDown ? 1 : 0);
    } else if (index > range.endIndex) {
      index = isDown ? range.endIndex : range.endIndex - 1;
    } else {
      index += isDown ? 1 : -1;
    }

    const container = document.querySelector(
      `[data-virtuoso-scroller] [data-item-index="${index}"]`,
    ) as HTMLDivElement | null;
    if (container) {
      hotkeyIndex.current = index;
      container.scrollIntoView({ behavior: "smooth", block: "center" });

      const node = container.querySelector(".focusable");
      if (node instanceof HTMLElement) {
        node.focus({ preventScroll: true });
      }
    } else {
      document.scrollingElement?.scrollBy({
        left: 0,
        top: isDown ? 200 : -200,
        behavior: "smooth",
      });
    }
  };

  useHotkeys(
    "down, j",
    () => handleHotkeyScroll("down"),
    { preventDefault: true },
    [data.length, range],
  );
  useHotkeys(
    "up, k",
    () => handleHotkeyScroll("up"),
    { preventDefault: true },
    [data.length, range],
  );

  return (
    <Virtuoso
      ref={ref}
      id={id}
      useWindowScroll={useWindowScroll}
      className={className}
      data={data}
      data-testid="virtuoso"
      atTopStateChange={atTopStateChange}
      atTopThreshold={atTopThreshold}
      atBottomStateChange={atBottomStateChange}
      firstItemIndex={firstItemIndex}
      startReached={onScrollToTop}
      endReached={handleEndReached}
      isScrolling={(isScrolling) => isScrolling && onScroll && onScroll()}
      itemContent={renderItem}
      initialTopMostItemIndex={initialIndex}
      rangeChanged={handleRangeChange}
      {...(increaseViewportBy !== undefined ? { increaseViewportBy } : {})}
      style={style}
      context={{
        listClassName: className,
        itemClassName,
      }}
      components={{
        Header: () => <>{prepend}</>,
        Footer: () => <>{append}</>,
        ScrollSeekPlaceholder: Placeholder as any,
        EmptyPlaceholder: () => renderEmpty(),
        List,
        Item,
      }}
    />
  );
};

export default ScrollableList;
export { readSavedScrollPosition };
export type { ScrollableListProps };
